/**
 * Programmatic SEO matrix: platform × suspension-reason.
 *
 * Each (platform, reason) combination generates a URL:
 *   /appeal/:platform/:reason
 *
 * We keep this hand-curated (not auto-cross-joined) so every page renders
 * with real, distinct, non-thin content. All copy is neutral/compliance-safe
 * ("prepare documentation", not "restore" or "guarantee").
 *
 * Reason bodies MUST be unique per (platform, reason) pair to avoid Google's
 * "similar content" flag.
 */

export const PLATFORMS = {
  instagram: {
    slug: "instagram",
    name: "Instagram",
    parent: "Meta",
    color: "from-pink-500 to-purple-600",
    officialAppealUrl: "https://help.instagram.com/contact/606967319425038",
    servicePath: "/services/instagram-account-appeal",
  },
  facebook: {
    slug: "facebook",
    name: "Facebook",
    parent: "Meta",
    color: "from-blue-500 to-blue-700",
    officialAppealUrl: "https://www.facebook.com/help/contact/260749603972907",
    servicePath: "/services/facebook-account-appeal",
  },
  tiktok: {
    slug: "tiktok",
    name: "TikTok",
    parent: "ByteDance",
    color: "from-black to-pink-500",
    officialAppealUrl: "https://support.tiktok.com/en/safety-hc/account-and-user-safety/account-suspended",
    servicePath: "/services/tiktok-account-appeal",
  },
  youtube: {
    slug: "youtube",
    name: "YouTube",
    parent: "Google",
    color: "from-red-500 to-red-700",
    officialAppealUrl: "https://support.google.com/youtube/answer/2802168",
    servicePath: "/services/youtube-account-appeal",
  },
  twitter: {
    slug: "twitter",
    name: "X (Twitter)",
    parent: "X Corp",
    color: "from-gray-800 to-black",
    officialAppealUrl: "https://help.twitter.com/en/forms/account-access/appeals",
    servicePath: "/services/x-account-appeal",
  },
  linkedin: {
    slug: "linkedin",
    name: "LinkedIn",
    parent: "Microsoft",
    color: "from-blue-700 to-blue-900",
    officialAppealUrl: "https://www.linkedin.com/help/linkedin/ask/TS-RAA",
    servicePath: "/services/linkedin-account-appeal",
  },
  snapchat: {
    slug: "snapchat",
    name: "Snapchat",
    parent: "Snap Inc",
    color: "from-yellow-400 to-yellow-600",
    officialAppealUrl: "https://support.snapchat.com/en-US/i-need-help",
    servicePath: "/snapchat-account-dispute",
  },
  reddit: {
    slug: "reddit",
    name: "Reddit",
    parent: "Reddit Inc",
    color: "from-orange-500 to-red-600",
    officialAppealUrl: "https://www.reddithelp.com/hc/en-us/requests/new",
    servicePath: "/reddit-account-dispute",
  },
  "google-business": {
    slug: "google-business",
    name: "Google Business Profile",
    parent: "Google",
    color: "from-blue-500 to-green-500",
    officialAppealUrl: "https://support.google.com/business/gethelp",
    servicePath: "/services/google-business-profile-suspended",
  },
  "facebook-ads": {
    slug: "facebook-ads",
    name: "Facebook Ads",
    parent: "Meta",
    color: "from-blue-600 to-indigo-700",
    officialAppealUrl: "https://www.facebook.com/business/help/support",
    servicePath: "/services/facebook-business-manager-disabled",
  },
};

/**
 * Each reason has a canonical slug, display label, one-line summary,
 * a longer intro paragraph, and platform-specific body variants.
 *
 * `bodies` is a map keyed by platform slug — only defined platforms
 * receive a page for this reason. Undefined = no page (avoids thin content).
 */
export const REASONS = {
  "community-guidelines": {
    slug: "community-guidelines",
    label: "Community Guidelines Violation",
    tagline: "The most common enforcement category. Automated + human review.",
    intro:
      "A Community Guidelines violation is the platform's most common enforcement reason. The notice usually doesn't tell you which sub-category triggered it, which is why matching your case to the platform's own published policy language is the single most important step in any appeal.",
    bodies: {
      instagram:
        "Meta enforces 14 Community Guideline categories on Instagram. Automated detection flags the first violation; a human reviewer confirms escalations. Common triggers: nudity/sexual activity (§Adult content), hate speech (§Hateful conduct), coordinated inauthentic behavior (§Fake engagement), and IP-related content (§Intellectual property). Your appeal must reference the exact sub-policy — Meta's reviewers work from category codes, not free-form prose.",
      facebook:
        "Facebook Community Standards define 22 enforcement areas. Business Pages and personal accounts follow the same standards but escalate differently. Documentation for a Facebook Community Standards appeal should reconstruct the timeline of the flagged content, cite the specific Standard, and — if applicable — invoke Facebook's newsworthiness or public-interest allowances.",
      tiktok:
        "TikTok Community Guidelines cluster around 10 policy pillars. Notice: 'Multiple Community Guidelines Violations' typically means three or more separate strikes over a rolling window. A structured appeal that groups each strike, cites the specific pillar, and provides context per strike gives reviewers something to actually work with.",
      youtube:
        "YouTube Community Guidelines strikes fall into 4 categories: Spam & deceptive practices, Sensitive content, Violent/dangerous content, and Regulated goods. A third strike within 90 days terminates the channel. Appeal documentation must address each strike independently — a blanket 'I did nothing wrong' will be rejected without review.",
      twitter:
        "X (Twitter) Rules cover 5 primary areas: authenticity, safety, privacy, IP, and platform manipulation. Enforcement can be temporary lock, permanent suspension, or content-level removal. Documentation should reconstruct the specific tweet(s) flagged, cite the applicable Rule, and — if suspension was based on 'severe' violations — provide context that a human reviewer can weigh.",
      linkedin:
        "LinkedIn's Professional Community Policies emphasize authentic identity, professional conduct, and no automation. Restrictions frequently follow LinkedIn's automated invite-throttling or 'unusual activity' detection. Appeal documentation should establish your professional identity with independent verification (employer letter, LinkedIn Learning certificate, published articles).",
    },
  },
  "impersonation": {
    slug: "impersonation",
    label: "Impersonation / Identity",
    tagline: "Someone claimed you impersonated a person, brand, or entity.",
    intro:
      "Impersonation enforcement is triggered when a third party (or the platform's automated system) flags your account as pretending to be someone else. Successful appeals almost always turn on documented identity verification — government ID, business filings, trademark registrations, or matched contact info.",
    bodies: {
      instagram:
        "Instagram impersonation appeals require the platform's Impersonation Report form + government-issued photo ID. If the account represents a business, add EIN letter, DBA filing, or state business registration. If the account was flagged by a competing account, include the competing account's URL in your appeal.",
      facebook:
        "Facebook impersonation appeals split into two paths: personal (real-name policy) and business (Page authenticity). Personal accounts flagged as fake typically need government ID with photo, DOB, and full legal name matching the account. Business Pages need business registration documents and, for verified Pages, prior verification records.",
      tiktok:
        "TikTok's impersonation policy covers Verified accounts, brands, and celebrities. Documentation must show either (a) authorization from the represented entity, or (b) that your account is clearly a fan/commentary/parody account (which TikTok allows if labeled).",
      linkedin:
        "LinkedIn impersonation is often triggered by name conflicts with executives or public figures. Documentation should include: government ID, employer verification email from a corporate domain, and — if applicable — evidence of prior mentions in press or industry publications.",
      twitter:
        "X impersonation policy allows parody, commentary, and fan accounts if 'clearly labeled' in both bio and display name. Verified impersonation appeals typically require: government ID, employer letter, and — for public figures — verifiable third-party references.",
    },
  },
  "spam-inauthentic": {
    slug: "spam-inauthentic",
    label: "Spam / Inauthentic Behavior",
    tagline: "Bulk activity, follow/unfollow patterns, or automation detected.",
    intro:
      "Spam / inauthentic behavior enforcement is almost entirely algorithmic. The platform's ML systems detect patterns — mass follows, hashtag stuffing, cross-account activity, IP overlap — and disable at scale. Appeal documentation must explain the legitimate business reason behind the flagged pattern.",
    bodies: {
      instagram:
        "Instagram flags for 'inauthentic activity' typically hit at 50-200 follows/hour, hashtag stuffing (30+ hashtags with low relevance), or repeated engagement patterns. Successful appeals document (a) that you weren't using third-party automation tools, (b) your genuine engagement funnel, and (c) recent Instagram-approved growth activities.",
      facebook:
        "Facebook spam enforcement often hits Pages that share the same content repeatedly, use fake engagement pods, or run offers that trigger 'clickbait' classifiers. Appeal should reconstruct the specific content flagged and clearly separate legitimate promotional activity from prohibited behavior.",
      tiktok:
        "TikTok spam/fake engagement enforcement targets accounts that: (a) buy views/followers, (b) participate in coordinated engagement schemes, or (c) repost content in bulk. Successful appeals demonstrate organic growth patterns using TikTok Analytics screenshots and content-original evidence.",
      linkedin:
        "LinkedIn 'unusual activity' restrictions often follow: bulk invite sending (200+/day), scraped connection lists, or LinkedIn Automation tool usage. Appeals should confirm no third-party tool usage and provide context on your networking approach.",
      twitter:
        "X platform manipulation enforcement targets: automated posting, coordinated inauthentic behavior, and bulk follows. Appeals should provide context on your posting workflow (native vs API vs approved third-party) and clarify any automated components.",
      reddit:
        "Reddit spam enforcement follows the 9:1 rule (self-promotion should be less than 10% of activity). Karma-farming, vote manipulation, and cross-subreddit posting can trigger site-wide bans. Appeals should document your community participation history via reddit.com/user/[username].",
    },
  },
  "hacked-compromised": {
    slug: "hacked-compromised",
    label: "Hacked or Compromised Account",
    tagline: "Suspicious activity detected — platform preemptively disabled.",
    intro:
      "When platforms detect signs of account compromise (login from new country, sudden mass DMs, unusual API activity), they often preemptively disable the account to prevent further damage. This is not a policy violation — it's an identity recovery flow. Documentation must prove YOU own the account.",
    bodies: {
      instagram:
        "Instagram's hacked-account recovery uses the 'My account was hacked' form. You'll need to submit: a video selfie (Instagram-controlled prompts), your associated email/phone, past login IP evidence, and — if 2FA was breached — the original 2FA setup timestamp.",
      facebook:
        "Facebook's hacked-account recovery is at facebook.com/hacked. If you can still access your email, use the standard flow. If not, the identity confirmation flow uses government ID + a trusted-contact reference (Facebook's Trusted Contacts feature).",
      tiktok:
        "TikTok's hacked-account recovery requires the associated phone number OR email. If both are lost, TikTok's manual review requires: original signup date, first three creators followed, and a linked social account (Instagram/YouTube).",
      snapchat:
        "Snapchat compromised-account recovery uses support.snapchat.com. You'll need: original signup email/phone, best friend list from the last 30 days, and last successful login location.",
      twitter:
        "X hacked-account recovery uses help.twitter.com. Recovery requires: original signup email, associated phone, last login IP context, and — for suspended accounts — a government ID for identity confirmation.",
      linkedin:
        "LinkedIn hacked-account recovery requires: government ID, current employer verification, and (if premium) billing records for account confirmation.",
    },
  },
  "copyright-dmca": {
    slug: "copyright-dmca",
    label: "Copyright / DMCA Complaint",
    tagline: "A third party filed a copyright takedown against your content.",
    intro:
      "Copyright enforcement is driven by third-party DMCA takedown notices. The platform is legally required to remove the content pending counter-notice. If you own the content OR have proper licensing, the DMCA counter-notification is your appeal path.",
    bodies: {
      instagram:
        "Instagram DMCA counter-notice: file at help.instagram.com/contact/552695131608132. Must include: your identification and contact info, a description of the removed content, a good-faith statement that removal was mistake or misidentification, consent to jurisdiction in your federal district, and your signature.",
      youtube:
        "YouTube Copyright strikes vs Content ID claims are different — appeal paths differ. Strikes use DMCA counter-notice (studio.youtube.com > Copyright); Content ID uses the dispute flow in YouTube Studio. Three strikes = channel termination, so appeal windows matter.",
      facebook:
        "Facebook DMCA counter-notice: facebook.com/help/contact/634636770043106. Same DMCA elements required. Facebook also runs Rights Manager, which is separate from user-filed DMCA claims — Rights Manager appeals go through a different form.",
      tiktok:
        "TikTok copyright dispute: submitted via the TikTok Copyright Counter-Notification form. TikTok's automated audio-recognition system also removes videos with unauthorized commercial music — those go through a separate 'music dispute' path.",
      twitter:
        "X copyright complaint counter-notice: help.twitter.com/en/forms/ipi/dmca. Includes standard DMCA counter-notice elements plus X-specific fields for account information.",
    },
  },
  "advertising-policy": {
    slug: "advertising-policy",
    label: "Advertising / Ad Account Policy",
    tagline: "Ad account, business manager, or ad-serving privileges disabled.",
    intro:
      "Advertising policy enforcement is separate from personal-account policy enforcement. Ad accounts get disabled for: circumventing systems, restricted-goods promotion, personal attributes, unauthorized business practices, or account-level trust signals falling below threshold.",
    bodies: {
      "facebook-ads":
        "Meta Business Manager / Facebook Ads restrictions are the highest-stakes account issue in the ecosystem. 'Circumventing systems' is the most common code and is often triggered by IP/device fingerprinting overlap with previously-disabled accounts. Appeal path is via Business Support Home; documentation should include business registration, prior working ad account history, and — for agencies — Business Manager admin chain.",
      instagram:
        "Instagram ad accounts inherit their parent Business Manager's policy status. If the personal account was disabled first, the ad account cascades. Appeal path routes through Meta Business Support Home, not the personal-account appeal path.",
      "google-business":
        "Google Ads / Business Profile suspensions cluster in categories: misrepresentation, dangerous products, unapproved substances, and Circumventing Systems. Appeal path is via Google Ads Help > Contact Us. Documentation typically requires business verification, website ownership proof, and — for local businesses — proof of physical premises.",
      tiktok:
        "TikTok Ads Manager suspensions typically follow: prohibited product ads (weight loss, financial services, adult), community-guideline overlap on creative, or account-trust-score drops. Appeal path is via TikTok Ads Manager's in-app support form.",
    },
  },
};

/**
 * Build the (platform, reason) pairs that actually exist based on
 * REASONS[reason].bodies[platform] being defined.
 */
export function getAllAppealMatrixPairs() {
  const pairs = [];
  for (const reason of Object.values(REASONS)) {
    for (const platformSlug of Object.keys(reason.bodies)) {
      if (PLATFORMS[platformSlug]) {
        pairs.push({
          platform: PLATFORMS[platformSlug],
          reason,
          body: reason.bodies[platformSlug],
        });
      }
    }
  }
  return pairs;
}

/**
 * Find a specific (platform, reason) pair or return null.
 */
export function getAppealMatrixPair(platformSlug, reasonSlug) {
  const platform = PLATFORMS[platformSlug];
  const reason = REASONS[reasonSlug];
  if (!platform || !reason) return null;
  const body = reason.bodies[platformSlug];
  if (!body) return null;
  return { platform, reason, body };
}

/**
 * Related pairs for internal linking (topic cluster).
 * Returns up to `limit` other pairs — first prioritizes same platform,
 * then same reason.
 */
export function getRelatedPairs(platformSlug, reasonSlug, limit = 6) {
  const all = getAllAppealMatrixPairs().filter(
    (p) => !(p.platform.slug === platformSlug && p.reason.slug === reasonSlug)
  );
  const samePlatform = all.filter((p) => p.platform.slug === platformSlug);
  const sameReason = all.filter(
    (p) => p.reason.slug === reasonSlug && p.platform.slug !== platformSlug
  );
  const rest = all.filter(
    (p) => p.platform.slug !== platformSlug && p.reason.slug !== reasonSlug
  );
  return [...samePlatform, ...sameReason, ...rest].slice(0, limit);
}
